/**
 * A date picker drawn in the studio's own colours.
 *
 * The same argument as `Select`: `<input type="date">` hands the calendar to
 * the browser, and the browser's calendar is the one part of a page no
 * stylesheet reaches. On Windows it arrives as a grey system panel in the
 * middle of a pink-and-ink app, on iOS as a spinning drum, and on Firefox the
 * field shows `dd/mm/yyyy` whatever the studio has set its date format to.
 *
 * So the trigger shows the date the way the rest of Hapi writes dates — the
 * signed-in person's own format, through `formatDateShort` — and the calendar
 * is this app's. The month grid is `monthMatrix`, the same one the Calendar
 * page draws itself on, so a month looks the same wherever you meet it and
 * the week starts where the studio says it starts.
 *
 * It is a drop-in for the input it replaces: `value` and `onChange` speak
 * `YYYY-MM-DD`, and `onChange` is handed an event-shaped object whose
 * `target.value` is the new date, which is all any handler in this app reads.
 * An empty string is no date, and stays possible — most of these fields are
 * things like "promised for", which is often not known yet.
 *
 * The popover is portalled to the body so a drawer's or a table's overflow
 * never clips it, and it sits at z-[400]: above the drawers and modals at
 * z-[200], which is where the Select's list had to go for the same reason.
 */

import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ChangeEvent } from 'react';
import { createPortal } from 'react-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { formatDateShort } from '../lib/format';
import { monthMatrix, weekdayNames } from '../lib/monthGrid';
import { cn } from '../lib/cn';

/** `YYYY-MM-DD` for a local date, never `toISOString` — that shifts the day. */
export function isoOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** A `YYYY-MM-DD` read as a local date, or null if it is not one. */
function dateOf(iso: string | undefined): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '');
  if (m === null) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

export interface DatePickerProps {
  value?: string | undefined;
  onChange?: ((e: ChangeEvent<HTMLInputElement>) => void) | undefined;
  disabled?: boolean | undefined;
  /** Shown when there is no date. */
  placeholder?: string;
  /** Offer "Today" and "Tomorrow" above the grid. On by default. */
  shortcuts?: boolean;
  min?: string | undefined;
  max?: string | undefined;
  name?: string | undefined;
  id?: string | undefined;
  className?: string | undefined;
  /**
   * Open the calendar the moment this mounts, rather than waiting for a click.
   *
   * For the sheet, where the cell is already being edited by the time the
   * picker exists — asking for one more click to see the calendar would make
   * the studio's picker slower than the browser's.
   */
  openOnMount?: boolean;
  /** The calendar closed without choosing a different date. */
  onDismiss?: (() => void) | undefined;
  'aria-label'?: string | undefined;
  'aria-labelledby'?: string | undefined;
}

export function DatePicker({
  value,
  onChange,
  disabled,
  placeholder = 'Pick a date',
  shortcuts = true,
  min,
  max,
  name,
  id,
  className,
  openOnMount = false,
  onDismiss,
  ...rest
}: DatePickerProps) {
  const { weekStartsOn } = useWorkspace();
  const chosen = dateOf(value);
  const today = new Date();
  const todayIso = isoOf(today);

  const [open, setOpen] = useState(openOnMount);
  const [cursor, setCursor] = useState(() => chosen ?? today);
  const [place, setPlace] = useState<{ left: number; top: number; up: boolean } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  // Reopening on a different value should land on that value's month, not on
  // whatever month was last flicked to.
  useEffect(() => {
    if (!open) setCursor(chosen ?? new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, open]);

  const measure = useCallback(() => {
    const r = button.current?.getBoundingClientRect();
    if (r === undefined) return;
    const PANEL = 330;
    const below = window.innerHeight - r.bottom;
    const up = below < PANEL && r.top > below;
    setPlace({
      left: Math.max(8, Math.min(r.left, window.innerWidth - 296 - 8)),
      top: up ? r.top : r.bottom,
      up
    });
  }, []);

  // Opened on mount, the panel has nowhere to go until the trigger has been
  // measured, and a click is what usually measures it.
  useLayoutEffect(() => {
    if (openOnMount) measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Nobody clicked the trigger, so nothing has the keyboard yet and Escape
  // would go to the page behind. The panel takes it.
  useEffect(() => {
    if (openOnMount && open && place !== null) panel.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [place === null]);

  useLayoutEffect(() => {
    if (!open) return;
    const onMove = () => measure();
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!button.current?.contains(t) && !panel.current?.contains(t)) {
        setOpen(false);
        onDismiss?.();
      }
    };
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    document.addEventListener('pointerdown', onDown);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open, measure, onDismiss]);

  function say(iso: string) {
    const target = { value: iso, name: name ?? '' };
    onChange?.({ target, currentTarget: target } as unknown as ChangeEvent<HTMLInputElement>);
  }

  function pick(iso: string) {
    setOpen(false);
    button.current?.focus();
    // Picking the day that was already there is a way of saying "leave it" —
    // whoever is listening for the close needs to hear about it either way.
    if (iso !== (value ?? '')) say(iso);
    else onDismiss?.();
  }

  function show() {
    if (disabled === true) return;
    setCursor(chosen ?? new Date());
    measure();
    setOpen(true);
  }

  const outOfRange = (iso: string) =>
    (min !== undefined && min !== '' && iso < min) || (max !== undefined && max !== '' && iso > max);

  const cells = monthMatrix(cursor.getFullYear(), cursor.getMonth(), weekStartsOn);
  const headings = weekdayNames(weekStartsOn);
  const monthName = cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const step = (by: number) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + by, 1));

  return (
    <>
      <button
        ref={button}
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={rest['aria-label']}
        aria-labelledby={rest['aria-labelledby']}
        onClick={() => (open ? setOpen(false) : show())}
        onKeyDown={(e) => {
          if (!open && ['ArrowDown', 'Enter', ' '].includes(e.key)) {
            e.preventDefault();
            show();
          }
        }}
        className={cn(
          'inline-flex cursor-pointer items-center gap-2 text-left disabled:cursor-not-allowed disabled:opacity-60',
          className,
        )}
      >
        <CalendarDays aria-hidden className="h-4 w-4 shrink-0 opacity-60" />
        <span className={cn('min-w-0 flex-1 truncate', chosen === null && 'opacity-60')}>
          {chosen === null ? placeholder : formatDateShort(value ?? '')}
        </span>
        {chosen !== null && disabled !== true && (
          // A date that was set by mistake has to be removable, and "clear the
          // field" is not something a calendar grid can say.
          <span
            role="button"
            tabIndex={-1}
            aria-label="Clear the date"
            onClick={(e) => {
              e.stopPropagation()
              say('')
            }}
            className="shrink-0 rounded p-0.5 opacity-50 transition-opacity hover:opacity-100"
          >
            <X aria-hidden className="h-3.5 w-3.5" />
          </span>
        )}
      </button>
      {name !== undefined && <input type="hidden" name={name} value={value ?? ''} />}

      {open &&
        place !== null &&
        createPortal(
          <div
            ref={panel}
            role="dialog"
            tabIndex={-1}
            aria-label="Pick a date"
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setOpen(false)
                button.current?.focus()
                onDismiss?.()
              }
            }}
            style={{
              position: 'fixed',
              left: place.left,
              width: 296,
              ...(place.up ? { bottom: window.innerHeight - place.top + 4 } : { top: place.top + 4 }),
            }}
            className="z-[400] rounded-xl border border-shell-border bg-shell p-3 shadow-pop"
          >
            <div className="mb-2 flex items-center justify-between">
              <button
                type="button"
                onClick={() => step(-1)}
                aria-label="The month before"
                className="rounded-lg p-1.5 text-shell-text hover:bg-shell-border/50"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm font-semibold text-shell-text-active">{monthName}</span>
              <button
                type="button"
                onClick={() => step(1)}
                aria-label="The month after"
                className="rounded-lg p-1.5 text-shell-text hover:bg-shell-border/50"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-0.5">
              {headings.map((d) => (
                <div key={d} className="pb-1 text-center text-[10px] font-semibold uppercase tracking-wide text-shell-text opacity-70">
                  {d}
                </div>
              ))}
              {cells.map(({ date, inMonth }) => {
                const iso = isoOf(date);
                const selected = iso === (value ?? '');
                const barred = outOfRange(iso);
                return (
                  <button
                    key={iso}
                    type="button"
                    disabled={barred}
                    aria-current={iso === todayIso ? 'date' : undefined}
                    aria-pressed={selected}
                    onClick={() => pick(iso)}
                    className={cn(
                      'h-8 rounded-lg text-sm tabular-nums transition-colors',
                      inMonth ? 'text-shell-text-active' : 'text-shell-text opacity-40',
                      !barred && !selected && 'hover:bg-brand-500/10',
                      iso === todayIso && !selected && 'font-bold text-brand-500 ring-1 ring-inset ring-brand-500/40',
                      selected && 'bg-brand-500 font-semibold text-white',
                      barred && 'cursor-not-allowed opacity-25',
                    )}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>

            {shortcuts && (
              <div className="mt-2 flex gap-1.5 border-t border-shell-border pt-2">
                {[
                  { label: 'Today', at: 0 },
                  { label: 'Tomorrow', at: 1 },
                  { label: 'Next week', at: 7 },
                ].map(({ label, at }) => {
                  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + at);
                  const iso = isoOf(d);
                  return (
                    <button
                      key={label}
                      type="button"
                      disabled={outOfRange(iso)}
                      onClick={() => pick(iso)}
                      className="flex-1 rounded-lg border border-shell-border px-2 py-1.5 text-xs text-shell-text transition-colors hover:border-brand-500/50 hover:text-shell-text-active disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
